import React, { useState, useEffect, useCallback } from "react"
import Link from "next/link"
import styled from "styled-components"
import { observer } from "mobx-react"
import dynamic from "next/dynamic"
import { useTranslations } from "next-intl"
import {useRouter} from "next/router"
import DesktopContainer from "~/components/DesktopContainer"
import Spacer from "~/components/Spacer"
import Hamburger from "./Hamburger"
import WinItButton from "~/components/WinItButton"
import useInject from "~/hooks/useInject"
import payNYCTicketsLogo from "~/public/static/images/png/PayNYC.png"
import winitLogo from "~/public/static/images/svg/logo-shadow.svg"
import winitLawLogo from '~/public/static/images/svg/WinitLawLogo.svg'
import HeaderSupportPhoneNumber from "~/components/altHeaders/HeaderSupportPhoneNumber"
import PHONE_NUMBERS from "~/components/altHeaders/phoneNumbers"
import SupportPhoneNumber from "~/components/altHeaders/SupportPhoneNumber"
import phoneNumberIcon from "~/public/static/images/svg/phoneIconSquare.svg"

const LoginModal = dynamic(() => import("~/components/auth/LoginModal"))
const SignupModal = dynamic(() => import("~/components/auth/SignUpModal"))

export const minHeightDesktop = "70px"
export const minHeightMobile = "50px"

const HeaderContainer = styled.div`
  border-bottom: solid 1px ${props => props.theme.colors.old.lightBorder};
  min-height: ${minHeightDesktop};
  display: flex;
  flex-shrink: 0;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  position: sticky;
  top: 0;
  background: #FFF;
  z-index: 9;
  ${props => props.theme.media.mobile`
    min-height: ${minHeightMobile};
  `};
`

const MainHeaderContainer = styled.div`
  padding: 10px 0;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  ${props => props.theme.media.mobile`
    height: 56px;
  `};
`

const LogoContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
`

const LogoWinitImg = styled.img`
  height: 35px;
  display: block;
`
const LogoWinitLawImg = styled.img`
  height: 35px;
  display: block;
`

const Row = styled.div`
  width: 100%;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: nowrap;
  max-width: 1000px;
  ${props => props.theme.media.mobile`
    min-height: 50px;
  `};
`

const LinkContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`

const MobileMenuRow = styled.div`
  opacity: ${props => (props.open ? "1" : "0")};
  transition: max-height 0.4s ease-in-out, opacity 0.4s ease-in-out;
  max-height: ${props => (props.open ? "1000px" : "0px")};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  align-items: center;
`

const NavLink = styled.a`
  display: inline-flex;
  font-size: 0.75em;
  color: #8a8a8a;
  margin: 0 0.7em;
  ${props => props.theme.media.smallDesktop`
    margin: 0.6em 0;
  `};
  cursor: pointer;
  font-family: ${props => props.theme.fonts.avenirNextRegular};
  text-decoration: none;
  text-align: center;
  &:hover {
    text-decoration: underline;
  }
`

const RenderLinks = ({ links, isMobile, showSubmitTicket, isLoggedIn, logout }) => {
  const translate = useTranslations('header')

  return (
    <>
      {links.map(item => {
        if (item.external) {
          return (
            <NavLink href={item.href} key={item.title}>
              {item.title}
            </NavLink>
          )
        }
        if (item.href) {
          return (
            <Link href={item.href} key={item.title} passHref>
              <NavLink>{item.title}</NavLink>
            </Link>
          )
        }
        if (item.onClick) {
          return (
            <NavLink onClick={item.onClick} key={item.title}>
              {item.title}
            </NavLink>
          )
        }
      })}
      {isLoggedIn ? (
        <>
          <NavLink onClick={logout}>{translate('signOut')}</NavLink>
        </>
      ) : (
        <>
          <LoginModal opener={({ onClick }) => (
            <WinItButton
              style={{ marginLeft: "0.7em" }}
              height={27}
              fontSize="0.75em"
              shape="square"
              onClick={onClick}
              padding="2px 15px"
            >
              {translate('login')}
            </WinItButton>
              )}
          />
          {!isMobile ? (
            <>
              <LoginModal opener={({ onClick }) => <NavLink onClick={onClick} />} />
              <SignupModal opener={({ onClick }) => <NavLink onClick={onClick} />} />
            </>
          ) : null}
        </>
      )}
    </>
  )
}

const mapStore = store => ({
  isMobile: store.ui.isMobile,
  isSmallDesktop: store.ui.isSmallDesktop,
  currentHost: store.ui.currentHost,
  isLoggedIn: store.auth.isLoggedIn,
  auth: store.auth,
  trafficSubmission: store.trafficSubmission
})

const Header = ({ showSubmitTicket = true, inFlow }) => {
  const {
    isMobile,
    isSmallDesktop,
    isLoggedIn,
    currentHost,
    auth,
    trafficSubmission
  } = useInject(mapStore)
  const translate = useTranslations('header')
  const router = useRouter()
  const shouldHideLinks = router.pathname === "/traffic-tickets"

  const [showMobileMenu, setShowMobileMenu] = useState(false)

  const links = shouldHideLinks ? [] : [
    {
      title: translate('links.knowledgeBase'),
      external: false,
      href: `/knowledge-base`
    },
    {
      title: translate('links.business'),
      external: false,
      href: `/business`
    },
    {
      title: translate('links.contact'),
      external: false,
      href: `/contact`
    },
    {
      title: translate('links.blog'),
      external: true,
      href: `/blog`
    }
  ]

  const toggleMobileMenu = useCallback(() => {
    setShowMobileMenu(!showMobileMenu)
  })

  useEffect(() => {
    if (!isSmallDesktop && showMobileMenu) {
      toggleMobileMenu()
    }
  }, [isSmallDesktop, showMobileMenu])

  let logoIMG = trafficSubmission.isStateAffiliatedWithWinitLaw  ? winitLawLogo : winitLogo
  let headerLinks = (
    <RenderLinks
      links={links}
      isLoggedIn={isLoggedIn}
      showSubmitTicket={showSubmitTicket}
      logout={auth.logout}
    />
)
  if ((currentHost && currentHost.includes('paynyctickets.com'))) {
    logoIMG = payNYCTicketsLogo
    headerLinks = null
  }

  const phoneNumber = PHONE_NUMBERS.TRAFFIC_FLOW[trafficSubmission.selectedState.shortName]
  || PHONE_NUMBERS.DEFAULT

  const mobileSupportPhoneNumber = (
    <HeaderSupportPhoneNumber
      phoneNumber={phoneNumber}
      icon={phoneNumberIcon}
      iconMargin="0px"
      hideNumber
    />
)

  return (
    <HeaderContainer>
      <MainHeaderContainer isMobile={isMobile}>
        <DesktopContainer>
          <Row isMobile={isMobile}>
            <LogoContainer>
              {inFlow ? (
                  trafficSubmission.isStateAffiliatedWithWinitLaw  ? (
                    <LogoWinitLawImg src={logoIMG} alt="WInitLaw Logo" />
                  ) : (
                    <LogoWinitImg src={logoIMG} alt="WinIt Logo" />
                  )
              ) : (
                <Link href="/">
                  <a>
                    {trafficSubmission.isStateAffiliatedWithWinitLaw ? (
                      <LogoWinitLawImg src={logoIMG} alt="WInitLaw Logo" />
                      ) : (
                        <LogoWinitImg src={logoIMG} alt="WinIt Logo" />
                      )}
                  </a>
                </Link>
              )}
            </LogoContainer>
            {inFlow ? (
              isSmallDesktop ?
                mobileSupportPhoneNumber
                  :
                <SupportPhoneNumber phoneNumber={phoneNumber} />
            ) : (
              <LinkContainer>
                {isSmallDesktop ? (
                  <>
                    {!isLoggedIn ? (
                      <SignupModal
                        opener={({ onClick }) => (
                          <WinItButton
                            style={{ marginRight: "0.7em" }}
                            height={27}
                            fontSize="0.75em"
                            shape="round"
                            onClick={onClick}
                            padding="2px 15px"
                          >
                            {translate('signUp')}
                          </WinItButton>
                        )}
                      />
                    ) : null}
                    {mobileSupportPhoneNumber}
                    <Spacer direction="width" mobile={16} desktop={16} />
                    <Hamburger toggle={toggleMobileMenu} open={showMobileMenu} />
                  </>
                ) : headerLinks}
              </LinkContainer>
            )}
          </Row>
        </DesktopContainer>
      </MainHeaderContainer>
      <DesktopContainer>
        <MobileMenuRow open={showMobileMenu}>
          <Spacer mobile={16} desktop={16} />
          <RenderLinks
            links={links}
            isLoggedIn={isLoggedIn}
            logout={auth.logout}
            showSubmitTicket={showSubmitTicket}
            isMobile
          />
          <Spacer mobile={16} desktop={16} />
        </MobileMenuRow>
      </DesktopContainer>
    </HeaderContainer>
  )
}

export default observer(Header)
